'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  BarChart3,
  Globe,
  Lock,
  Mail,
  User,
  Copy,
  CheckCheck,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


// ─── Mock credentials ─────────────────────────────────────────────────────────
const DEMO_CREDENTIALS = [
  { role: 'Administrator', email: 'admin@surveytrack.internal', password: 'Admin@Track2026', roleKey: 'admin' },
  { role: 'Staff', email: 'staff@surveytrack.internal', password: 'Staff@Track2026', roleKey: 'staff' },
];

interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupFormValues {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

interface CredentialBoxProps {
  onUse: (email: string, password: string) => void;
}

function CredentialBox({ onUse }: CredentialBoxProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (value: string, key: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  return (
    <div className="mt-5 rounded-xl border border-primary/20 bg-primary/5 overflow-hidden">
      <div className="px-3 py-2 border-b border-primary/15 flex items-center gap-2">
        <ShieldCheck size={13} className="text-primary" />
        <span className="text-xs font-medium text-primary">Demo Accounts — click Use to autofill</span>
      </div>
      <div className="divide-y divide-border/40">
        {DEMO_CREDENTIALS.map((cred) => (
          <div key={`cred-${cred.roleKey}`} className="px-3 py-2.5 flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded border ${
                  cred.roleKey === 'admin' ?'text-primary bg-primary/15 border-primary/30' :'text-muted-foreground bg-white/5 border-border'
                }`}>
                  {cred.role}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground font-mono truncate">{cred.email}</span>
                  <button
                    type="button"
                    onClick={() => handleCopy(cred.email, `email-${cred.roleKey}`)}
                    className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                    aria-label={`Copy ${cred.role} email`}
                  >
                    {copiedField === `email-${cred.roleKey}` ? (
                      <CheckCheck size={11} className="text-green-400" />
                    ) : (
                      <Copy size={11} />
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground font-mono">{cred.password}</span>
                  <button
                    type="button"
                    onClick={() => handleCopy(cred.password, `pass-${cred.roleKey}`)}
                    className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                    aria-label={`Copy ${cred.role} password`}
                  >
                    {copiedField === `pass-${cred.roleKey}` ? (
                      <CheckCheck size={11} className="text-green-400" />
                    ) : (
                      <Copy size={11} />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onUse(cred.email, cred.password)}
              className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary/15 text-primary border border-primary/25 hover:bg-primary/25 transition-all duration-150 active:scale-95"
            >
              Use
              <ChevronRight size={11} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AuthPageClient() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const loginForm = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signupForm = useForm<SignupFormValues>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', agreeTerms: false },
  });

  const handleAutofill = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    setAuthError(null);
  };

  const handleLogin = async (data: LoginFormValues) => {
    setIsSubmitting(true);
    setAuthError(null);
    // BACKEND INTEGRATION: POST /api/auth/login with { email, password, rememberMe }
    await new Promise((r) => setTimeout(r, 800));

    const valid = DEMO_CREDENTIALS.some(
      (c) => c.email === data.email && c.password === data.password
    );

    if (valid) {
      // BACKEND INTEGRATION: Store session token, redirect to dashboard
      window.location.href = '/';
    } else {
      setAuthError('Invalid credentials — use the demo accounts below to sign in');
    }
    setIsSubmitting(false);
  };

  const handleSignup = async (data: SignupFormValues) => {
    setIsSubmitting(true);
    setAuthError(null);
    // BACKEND INTEGRATION: POST /api/auth/register with { fullName, email, password }
    await new Promise((r) => setTimeout(r, 900));

    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      setIsSubmitting(false);
      return;
    }

    setSignupSuccess(true);
    setIsSubmitting(false);
  };

  const features = [
    { icon: BarChart3, label: 'Real-time status tracking', desc: 'Monitor complete, terminate, and quota events as they happen' },
    { icon: Globe, label: 'Automatic IP detection', desc: 'Captures respondent IP on every survey redirect for fraud analysis' },
    { icon: ShieldCheck, label: 'Role-based access control', desc: 'Admins can edit and delete; staff have secure view-only access' },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Ambient glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-primary/8 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-900/10 rounded-full blur-3xl translate-x-1/4 translate-y-1/4" />
      </div>

      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] flex-col justify-between p-10 xl:p-14 relative overflow-hidden border-r border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-purple-900/5" />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-2.5">
          <AppLogo size={36} />
          <span className="font-semibold text-xl tracking-tight text-foreground">SurveyTrack</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/20 text-primary border border-primary/30 ml-1">
            Internal
          </span>
        </div>

        {/* Hero content */}
        <div className="relative z-10 space-y-8">
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-foreground leading-tight tracking-tight">
              Survey response<br />
              <span className="text-primary">intelligence</span><br />
              at a glance.
            </h2>
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed max-w-sm">
              Parse survey redirects, capture IPs, and monitor completion quality across all your project IDs — all in one internal tool.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4">
            {features.map((feat) => {
              const Icon = feat.icon;
              return (
                <div key={`feat-${feat.label.replace(/ /g, '-')}`} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon size={15} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{feat.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom status strip */}
        <div className="relative z-10 flex items-center gap-2 text-xs text-muted-foreground">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <span>All systems operational</span>
          <span className="mx-2 text-border">·</span>
          <span className="font-mono">v2.4.1</span>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-8 justify-center">
            <AppLogo size={32} />
            <span className="font-semibold text-lg tracking-tight text-foreground">SurveyTrack</span>
          </div>

          {/* Mode toggle */}
          <div className="flex items-center gap-1 p-1 rounded-xl bg-muted border border-border mb-7">
            {(['login', 'signup'] as const).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => { setMode(m); setAuthError(null); setSignupSuccess(false); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  mode === m
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === 'login' ? 'Sign In' : 'Sign Up'}
              </button>
            ))}
          </div>

          {/* Login form */}
          {mode === 'login' && (
            <div className="glass-card rounded-2xl border border-border p-6 fade-in">
              <div className="mb-6">
                <h1 className="text-xl font-semibold text-foreground">Welcome back</h1>
                <p className="text-sm text-muted-foreground mt-1">Sign in to your SurveyTrack account</p>
              </div>

              {/* Auth error */}
              {authError && (
                <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-lg bg-red-500/10 border border-red-500/25 mb-5 fade-in">
                  <AlertCircle size={15} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-400 leading-relaxed">{authError}</p>
                </div>
              )}

              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4" noValidate>
                {/* Email */}
                <div>
                  <label htmlFor="login-email" className="block text-sm font-medium text-foreground mb-1.5">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      id="login-email"
                      type="email"
                      autoComplete="email"
                      {...loginForm.register('email', {
                        required: 'Email is required',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                      })}
                      className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary transition-colors"
                      placeholder="you@surveytrack.internal"
                    />
                  </div>
                  {loginForm.formState.errors.email && (
                    <p className="mt-1 text-xs text-red-400">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="login-password" className="block text-sm font-medium text-foreground mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      {...loginForm.register('password', {
                        required: 'Password is required',
                        minLength: { value: 6, message: 'Password must be at least 6 characters' },
                      })}
                      className="w-full pl-9 pr-10 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary transition-colors"
                      placeholder="••••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="mt-1 text-xs text-red-400">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>

                {/* Remember me */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      {...loginForm.register('rememberMe')}
                      className="rounded border-border bg-input accent-primary"
                    />
                    <span className="text-xs text-muted-foreground">Remember me for 30 days</span>
                  </label>
                  <button type="button" className="text-xs text-primary hover:text-primary/80 transition-colors">
                    Forgot password?
                  </button>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground border border-primary/50 transition-all duration-150 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                  style={{ minHeight: '42px' }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    'Sign In to SurveyTrack'
                  )}
                </button>
              </form>

              {/* Demo credentials */}
              <CredentialBox onUse={handleAutofill} />

              <p className="text-center text-xs text-muted-foreground mt-5">
                Need an account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('signup')}
                  className="text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Request access
                </button>
              </p>
            </div>
          )}

          {/* Signup form */}
          {mode === 'signup' && (
            <div className="glass-card rounded-2xl border border-border p-6 fade-in">
              {signupSuccess ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                    <ShieldCheck size={24} className="text-green-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-foreground mb-2">Request submitted</h2>
                  <p className="text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
                    Your account request has been sent to the SurveyTrack admin team. You'll be notified once access is approved.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setSignupSuccess(false); }}
                    className="mt-5 flex items-center gap-1.5 mx-auto text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    Back to Sign In
                    <ChevronRight size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <h1 className="text-xl font-semibold text-foreground">Request access</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                      Submit a request for a SurveyTrack account — an admin will approve it
                    </p>
                  </div>

                  {authError && (
                    <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-lg bg-red-500/10 border border-red-500/25 mb-5 fade-in">
                      <AlertCircle size={15} className="text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-400 leading-relaxed">{authError}</p>
                    </div>
                  )}

                  <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4" noValidate>
                    {/* Full name */}
                    <div>
                      <label htmlFor="signup-name" className="block text-sm font-medium text-foreground mb-1.5">
                        Full name
                      </label>
                      <div className="relative">
                        <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        <input
                          id="signup-name"
                          type="text"
                          autoComplete="name"
                          {...signupForm.register('fullName', {
                            required: 'Full name is required',
                            minLength: { value: 2, message: 'Name must be at least 2 characters' },
                          })}
                          className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary transition-colors"
                          placeholder="Priya Kapoor"
                        />
                      </div>
                      {signupForm.formState.errors.fullName && (
                        <p className="mt-1 text-xs text-red-400">{signupForm.formState.errors.fullName.message}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="signup-email" className="block text-sm font-medium text-foreground mb-1.5">
                        Work email
                      </label>
                      <p className="text-xs text-muted-foreground mb-1.5">
                        Use your company email address for access verification
                      </p>
                      <div className="relative">
                        <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        <input
                          id="signup-email"
                          type="email"
                          autoComplete="email"
                          {...signupForm.register('email', {
                            required: 'Email is required',
                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                          })}
                          className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary transition-colors"
                          placeholder="priya@surveytrack.internal"
                        />
                      </div>
                      {signupForm.formState.errors.email && (
                        <p className="mt-1 text-xs text-red-400">{signupForm.formState.errors.email.message}</p>
                      )}
                    </div>

                    {/* Password */}
                    <div>
                      <label htmlFor="signup-password" className="block text-sm font-medium text-foreground mb-1.5">
                        Password
                      </label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        <input
                          id="signup-password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          {...signupForm.register('password', {
                            required: 'Password is required',
                            minLength: { value: 8, message: 'Password must be at least 8 characters' },
                            pattern: {
                              value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                              message: 'Must contain uppercase, lowercase, and a number',
                            },
                          })}
                          className="w-full pl-9 pr-10 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary transition-colors"
                          placeholder="Min. 8 characters"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                      {signupForm.formState.errors.password && (
                        <p className="mt-1 text-xs text-red-400">{signupForm.formState.errors.password.message}</p>
                      )}
                    </div>

                    {/* Confirm password */}
                    <div>
                      <label htmlFor="signup-confirm" className="block text-sm font-medium text-foreground mb-1.5">
                        Confirm password
                      </label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        <input
                          id="signup-confirm"
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          {...signupForm.register('confirmPassword', {
                            required: 'Please confirm your password',
                          })}
                          className="w-full pl-9 pr-10 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-primary transition-colors"
                          placeholder="Repeat password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                        >
                          {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                      {signupForm.formState.errors.confirmPassword && (
                        <p className="mt-1 text-xs text-red-400">{signupForm.formState.errors.confirmPassword.message}</p>
                      )}
                    </div>

                    {/* Terms */}
                    <div>
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          {...signupForm.register('agreeTerms', {
                            required: 'You must accept the terms to continue',
                          })}
                          className="mt-0.5 rounded border-border bg-input accent-primary"
                        />
                        <span className="text-xs text-muted-foreground leading-relaxed">
                          I agree to the{' '}
                          <span className="text-primary hover:text-primary/80 cursor-pointer">Terms of Service</span>
                          {' '}and{' '}
                          <span className="text-primary hover:text-primary/80 cursor-pointer">Privacy Policy</span>
                        </span>
                      </label>
                      {signupForm.formState.errors.agreeTerms && (
                        <p className="mt-1 text-xs text-red-400">{signupForm.formState.errors.agreeTerms.message}</p>
                      )}
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground border border-primary/50 transition-all duration-150 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                      style={{ minHeight: '42px' }}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={15} className="animate-spin" />
                          Submitting request…
                        </>
                      ) : (
                        'Submit Access Request'
                      )}
                    </button>
                  </form>

                  <p className="text-center text-xs text-muted-foreground mt-5">
                    Already have access?{' '}
                    <button
                      type="button"
                      onClick={() => setMode('login')}
                      className="text-primary hover:text-primary/80 font-medium transition-colors"
                    >
                      Sign in
                    </button>
                  </p>
                </>
              )}
            </div>
          )}

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground mt-6">
            SurveyTrack is an internal tool.{' '}
            <Link href="/" className="text-primary hover:text-primary/80 transition-colors">
              View dashboard
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}